import { Container, Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { DisplayUtils } from '../../utils/DisplayUtils';
import { GHOST_COLOR, GHOST_COLOR_MIX } from '../Game';
import { BaseEntity } from './BaseEntity';
import { ChassisEntity } from './ChassisEntity';
import { VehicleEntity } from './VehicleEntity';

interface DecoPlacementData
{
    x: number,
    y: number,
    rotation: number,
    layers: {
        front: string,
        back: string
    }
}

export interface DecoData
{
    id: string,
    type: 'deco',
    coinPrice?: number,
    diamondPrice?: number,
    placement: Record<string, DecoPlacementData>
    unlocked?: boolean;
}

export class DecoEntity extends BaseEntity<DecoPlacementData>
{
    public vehicle: VehicleEntity;
    public sprite: Sprite;

    constructor(vehicle: VehicleEntity)
    {
        super();
        this.vehicle = vehicle;
        this.vehicle.chassis.physics.physicsFreeUpdateRunner.add(this as any);
        this.sprite = new Sprite();
        this.sprite.anchor.set(0.5);
        this.sprite.scale.set(0.75);
        this.view.addChild(this.sprite);
    }

    public build(data: DecoPlacementData, frotOrBack: 'front'|'back'): void
    {
        this.data = data;
        const textureId = data.layers[frotOrBack];

        if (!textureId)
        {
            this.hide();

            return;
        }
        if (!this.vehicle.chassis.data) throw new Error('Provided chassis has no data');
        this.sprite.texture = Shared.assets.getTexture(data.layers[frotOrBack]);
        this.sprite.x = data.x + this.vehicle.chassis.data.spriteOffsetX;
        this.sprite.y = data.y + this.vehicle.chassis.data.spriteOffsetY;
        this.sprite.rotation = data.rotation;
        this.show();
    }

    public updateColor(ghost: boolean): void
    {
        this.sprite.tint = DisplayUtils.mixColors(0xFFFFFF, GHOST_COLOR, ghost ? GHOST_COLOR_MIX : 0);
    }

    public physicsFreeUpdate(): void
    {
        if (!this.data || !this.sprite.visible) return;
        this.x = this.view.x = this.vehicle.chassis.physics.x;
        this.y = this.view.y = this.vehicle.chassis.physics.y;
        this.rotation = -this.vehicle.chassis.physics.angle;
        this.view.alpha = this.vehicle.chassis.view.alpha;
        this.view.renderable = this.vehicle.chassis.view.renderable;
    }

    public show(): void
    {
        this.sprite.visible = true;
        this.physicsFreeUpdate();
        this.sprite.updateTransform();
    }

    public hide(): void
    {
        this.sprite.visible = false;
    }

    public static mountInVehicle(data: DecoData | string, vehicle: VehicleEntity): void
    {
        if (!data)
        {
            vehicle.decoBack.hide();
            vehicle.decoFront.hide();

            return;
        }

        if (!vehicle.chassis.data) throw new Error('Provided chassis has no data');
        if (typeof data === 'string') data = Shared.assets.getData<DecoData>(`deco-${data}`);

        const placement = data.placement[vehicle.chassis.data.id];

        if (!placement)
        {
            console.log(`[DecoEntity] Deco "${data.id}" not elegible for "${vehicle.chassis.data.id}"`);

            return;
        }

        vehicle.decoBack.build(placement, 'back');
        vehicle.decoFront.build(placement, 'front');
    }

    public static mountIcon(data: DecoData | string, container?: Container): Container
    {
        if (!container) container = new Container();

        if (typeof data === 'string') data = Shared.assets.getData<DecoData>(`deco-${data}`);

        const sprite = Sprite.from(Shared.assets.getTexture(`deco-${data.id}`));

        container.addChild(sprite);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.75);

        return container;
    }

    public static mountInChassis(data: DecoData | string, chassis: ChassisEntity): void
    {
        if (!chassis.data) throw new Error('Provided chassis has no data');
        if (typeof data === 'string') data = Shared.assets.getData<DecoData>(`deco-${data}`);

        const placement = data.placement[chassis.data.id];

        if (!placement)
        {
            console.log(`[DecoEntity] Deco "${data.id}" not elegible for "${chassis.data.id}"`);

            return;
        }

        if (placement.layers.front)
        {
            const sprite = Sprite.from(Shared.assets.getTexture(placement.layers.front));

            sprite.anchor.set(0.5);
            sprite.scale.set(0.75);
            sprite.x = placement.x + chassis.data.spriteOffsetX;
            sprite.y = placement.y + chassis.data.spriteOffsetY;
            sprite.rotation = placement.rotation;
            chassis.view.addChild(sprite);
        }

        if (placement.layers.back)
        {
            const sprite = Sprite.from(Shared.assets.getTexture(placement.layers.back));

            sprite.anchor.set(0.5);
            sprite.scale.set(0.75);
            sprite.x = placement.x + chassis.data.spriteOffsetX;
            sprite.y = placement.y + chassis.data.spriteOffsetY;
            sprite.rotation = placement.rotation;
            chassis.view.addChildAt(sprite, 0);
        }
    }
}
